import type { InputHTMLAttributes } from "react"
import { forwardRef, useEffect } from "react"

import type { BoxProps } from "@mui/material"
import { Autocomplete, Box, ListItem, ListItemText } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { find, has, toString } from "lodash"

import { useUrlParams } from "@ggs/hooks"
import { setCountry, setCountrySelVisible, useDispatch, useSelector } from "@ggs/store"
import { SvgIcon, type iconKeys } from ".."
import defaultCountriesList from "./CountriesOptions.json"

type SelectOption = {
  label: string
  value: string
}

const UserCountrySelect = ({ countries = defaultCountriesList }) => {
  const dispatch = useDispatch()
  const countrySelVisible = useSelector((state) => state.settings.countrySelVisible)
  const country = useSelector((state) => state.settings.country)
  const { params } = useUrlParams({
    countrysel: "",
  })

  const onCountryChange = (option: SelectOption) => {
    if (option) {
      const isEurope = option?.value === "EU"
      const countryName = option?.label
      const countryCode = option?.value
      if (countryName && countryCode) {
        dispatch(
          setCountry({
            name: countryName,
            code: countryCode,
            isEurope: isEurope,
          })
        )
      }
    }
  }

  const currentCountryCode = country?.code
  const currentCountryOption = find(countries, { value: currentCountryCode })
  useEffect(() => {
    const flagValue = toString(params?.countrysel)
    if (has(params, "countrysel") && flagValue !== "") {
      const flagIng = parseInt(flagValue)
      const newVisibility = flagIng === 1
      dispatch(setCountrySelVisible(newVisibility))
    }
  }, [])

  if (!countrySelVisible) {
    return null
  }

  return (
    <Box component="form">
      <Autocomplete
        value={currentCountryOption}
        onChange={(_, newValue) => {
          if (newValue) {
            onCountryChange(newValue)
          }
        }}
        openOnFocus
        options={countries || []}
        renderInput={(params) => (
          <SearchInputField
            ref={params.InputProps.ref}
            placeholder="Your Country"
            aria-label="Your Country"
            icon="globe"
            inputProps={params.inputProps}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <ListItem
            disablePadding
            selected={selected}
            sx={(theme) => ({
              "border": `1px solid transparent`,
              "minHeight": "48px!important",
              "py": 3.5,
              "px": 4,
              "&.Mui-focused, &.Mui-selected": {
                bgcolor: `${theme.palette["secondary.200"].main}!important`,
                borderRadius: 1,
                color: `${theme.palette["secondary.800"].main}!important`,
              },
              "&.Mui-selected": {
                borderColor: theme.palette["secondary.300"].main,
              },
            })}
            {...props}
          >
            <ListItemText primaryTypographyProps={{ variant: "link.xs" }}>
              {option.label}
            </ListItemText>
          </ListItem>
        )}
        ListboxProps={{
          style: {
            maxHeight: 324,
          },
          sx: {
            p: 2,
          },
        }}
        slotProps={{
          popper: {
            sx: {
              minWidth: { sm: "276px!important" },
              zIndex: 4000,
            },
          },
          paper: {
            sx: {
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
              boxShadow: (theme) => `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
              fontSize: { xs: 10, md: 12 },
              my: 1,
            },
          },
        }}
      />
    </Box>
  )
}

export default UserCountrySelect

type InputFieldProps = {
  inputProps: InputHTMLAttributes<HTMLInputElement>
  icon: string
} & BoxProps<"input">

const SearchInputField = forwardRef<any, InputFieldProps>(({ inputProps, icon, ...props }, ref) => (
  <Box ref={ref} position="relative" sx={{ isolation: "isolate" }}>
    <Box
      component="input"
      type="text"
      sx={(theme) => ({
        ...theme.typography["body.sm"],
        "bgcolor": "transparent",
        "border": { xs: `1px solid ${theme.palette["neutral.200"].main}`, md: "0" },
        "borderRadius": { xs: 1.5, md: 0 },
        "color": theme.palette["neutral.700"].main,
        "display": "block",
        "fontWeight": 500,
        "height": 68,
        "p": 4,
        "pl": 11.5,
        "width": "100%",
        "&::placeholder": {
          color: theme.palette["neutral.700"].main,
          fontWeight: 400,
          opacity: 0.7,
        },

        "&:focus": {
          "bgcolor": theme.palette["secondary.200"].main,
          "color": theme.palette["secondary.800"].main,
          "outline": "none",

          "&::placeholder": {
            color: theme.palette["secondary.800"].main,
          },

          "& + svg": {
            color: theme.palette["secondary.800"].main,
          },
        },
      })}
      {...props}
      {...inputProps}
    />
    <SvgIcon
      variant={icon as iconKeys}
      size={16}
      sx={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", zIndex: 1 }}
    />
  </Box>
))

SearchInputField.displayName = "SearchInputField"
